import {
  ArrowLeftRight,
  Boxes,
  CreditCard,
  FileBarChart,
  FileText,
  Gem,
  HandCoins,
  Landmark,
  LayoutDashboard,
  PiggyBank,
  ScrollText,
  Settings,
  Tags,
  TrendingDown,
  TrendingUp,
  Users,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

/**
 * The navigation tree, as data.
 *
 * The sidebar, the command palette and the topbar title all read from this one
 * structure, so a new module is added in exactly one place and can never drift
 * between the three.
 */

export interface NavItem {
  kind: 'item'
  label: string
  path: string
  icon: LucideIcon
  /** Icon accent colour (hex) — gives the rail a distinct, colourful identity. */
  color?: string | undefined
  /** Matched by the command palette but never displayed — synonyms, old names. */
  keywords?: string | undefined
  /** Match the path exactly. Needed for `/`, which otherwise prefixes everything. */
  end?: boolean | undefined
}

export interface NavGroup {
  kind: 'group'
  /** Persistence key suffix: the expanded state lives at `cbs.nav.<id>`. */
  id: string
  label: string
  icon: LucideIcon
  color?: string | undefined
  children: readonly NavItem[]
}

export type NavEntry = NavItem | NavGroup

export interface NavSection {
  id: string
  /** Draws a hairline rule above the section. Groups are separated, items are not. */
  separated: boolean
  entries: readonly NavEntry[]
}

/*
 * Only shipped modules appear here. Placeholder destinations (Reports, Loans,
 * Items, Purchases, Sales, Settlements, Journal Vouchers, Custom Accounts) were
 * removed so the sidebar honestly reflects what is built — each is re-added the
 * moment its module ships, alongside its route.
 */
export const NAV_SECTIONS: readonly NavSection[] = [
  {
    id: 'overview',
    separated: false,
    entries: [
      {
        kind: 'item',
        label: 'Dashboard',
        path: '/',
        icon: LayoutDashboard,
        // The home item wears the brand orange; its active row washes orange too.
        color: '#e8843e',
        end: true,
        keywords: 'home overview',
      },
      {
        kind: 'item',
        label: 'Reports',
        path: '/reports',
        icon: FileBarChart,
        color: '#8b5cf6',
        keywords: 'profit loss balance sheet trial gst day book category contact statement',
      },
    ],
  },
  {
    id: 'masters',
    separated: true,
    entries: [
      {
        kind: 'item',
        label: 'Contacts',
        path: '/contacts',
        icon: Users,
        color: '#0ea5e9',
        keywords: 'customers vendors parties',
      },
      {
        kind: 'item',
        label: 'Categories',
        path: '/categories',
        icon: Tags,
        color: '#f59e0b',
        keywords: 'income expense heads buckets ledger accounts',
      },
      {
        kind: 'item',
        label: 'Bank Accounts',
        path: '/bank-accounts',
        icon: Landmark,
        color: '#10b981',
        keywords: 'cash banks statement ledger',
      },
    ],
  },
  {
    id: 'sales',
    separated: true,
    entries: [
      {
        kind: 'item',
        label: 'Invoices',
        path: '/invoices',
        icon: FileText,
        color: '#3b82f6',
        keywords: 'sales gst tax invoice bill customer receivable',
      },
    ],
  },
  {
    id: 'daybook',
    separated: true,
    entries: [
      { kind: 'item', label: 'Expenses', path: '/expenses', icon: TrendingDown, color: '#f43f5e', keywords: 'spend' },
      { kind: 'item', label: 'Incomes', path: '/incomes', icon: TrendingUp, color: '#22c55e', keywords: 'revenue' },
      {
        kind: 'item',
        label: 'Payments',
        path: '/payments',
        icon: CreditCard,
        color: '#14b8a6',
        keywords: 'receipts paid received',
      },
      { kind: 'item', label: 'Bank Transfers', path: '/bank-transfers', icon: ArrowLeftRight, color: '#06b6d4' },
      {
        kind: 'item',
        label: 'Loans & Advances',
        path: '/loans',
        icon: HandCoins,
        color: '#f97316',
        keywords: 'lend borrow advance repayment given taken',
      },
    ],
  },
  {
    id: 'growth',
    separated: true,
    entries: [
      {
        kind: 'item',
        label: 'Products',
        path: '/products',
        icon: Boxes,
        color: '#d946ef',
        keywords: 'saas apps android ios web revenue product image',
      },
      {
        kind: 'item',
        label: 'Investments',
        path: '/investments',
        icon: PiggyBank,
        color: '#84cc16',
        keywords: 'fd mutual fund stocks fixed asset inter company holdings',
      },
      {
        kind: 'item',
        label: 'Wealth Tracker',
        path: '/wealth',
        icon: Gem,
        color: '#a855f7',
        keywords: 'growth profit top clients net worth analytics',
      },
    ],
  },
  {
    id: 'admin',
    separated: true,
    entries: [
      {
        kind: 'item',
        label: 'Audit Log',
        path: '/audit-log',
        icon: ScrollText,
        color: '#64748b',
        keywords: 'activity history changes trail',
      },
      {
        kind: 'item',
        label: 'Settings',
        path: '/settings',
        icon: Settings,
        color: '#6b7280',
        keywords: 'preferences organization company users',
      },
    ],
  },
]

/** Every destination, flattened — the command palette's source list. */
export const NAV_ITEMS: readonly NavItem[] = NAV_SECTIONS.flatMap((section) =>
  section.entries.flatMap((entry) => (entry.kind === 'group' ? entry.children : [entry])),
)

/** The `g`-then-letter jumps. Documented in docs/00-roadmap.md Part 3. */
export const GO_TO_SHORTCUTS: ReadonlyArray<{ key: string; path: string; label: string }> = [
  { key: 'd', path: '/', label: 'Dashboard' },
  { key: 'e', path: '/expenses', label: 'Expenses' },
  { key: 'i', path: '/incomes', label: 'Incomes' },
  { key: 'b', path: '/bank-accounts', label: 'Bank Accounts' },
  { key: 'c', path: '/contacts', label: 'Contacts' },
  { key: 'r', path: '/reports', label: 'Reports' },
]

/** True when `pathname` is at or below `item`'s route. */
export function isItemActive(item: NavItem, pathname: string): boolean {
  if (item.end === true) return pathname === item.path
  return pathname === item.path || pathname.startsWith(`${item.path}/`)
}

export function groupContainsActive(group: NavGroup, pathname: string): boolean {
  return group.children.some((child) => isItemActive(child, pathname))
}

/**
 * The page title for the topbar. Falls back to the first path segment rather
 * than an empty header, so a route this file has not caught up with still reads
 * sensibly.
 */
export function titleForPath(pathname: string): string {
  // Longest path first, so `/purchases/bills` beats a future `/purchases`.
  const match = [...NAV_ITEMS]
    .sort((a, b) => b.path.length - a.path.length)
    .find((item) => isItemActive(item, pathname))
  if (match) return match.label

  const segment = pathname.split('/').filter(Boolean)[0]
  if (!segment) return 'Dashboard'
  return segment.replace(/-/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase())
}
